import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, ButtonText, Center, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import {
  ID_CAPTURE_GUIDE_RECTS,
  ID_CAPTURE_PREVIEW_ASPECT,
  type IdCaptureMode,
} from './IdCaptureGuideOverlays';
import {
  AcceptedDocuments, FaceDoDont, IdCheckProgress,
} from './IdCaptureUiParts';

/* Webcam preview backdrop tuned for camera-off placeholder feel.
 *
 * `object-fit: cover` (not `contain`) is REQUIRED: the alignment overlay is
 * positioned as fractions of this container, and the capture crops the same
 * fractions of the *visible* video. `cover` makes the container show exactly the
 * centred sub-rectangle of the sensor that the crop maths reconstructs, so the
 * outline the student aligns to is pixel-for-pixel the region that gets saved.
 * With `contain` the video would be letterboxed and the outline would float over
 * bars, guaranteeing a mismatched crop. */
/* eslint-disable react-native/no-color-literals */
const PREVIEW_VIDEO_BASE_STYLE = {
  width: '100%' as const,
  height: '100%' as const,
  objectFit: 'cover' as const,
  display: 'block' as const,
  backgroundColor: '#0e1218',
};
/* eslint-enable react-native/no-color-literals */

/**
 * Mirroring is a *selfie-only* convenience (so the student sees themselves the
 * way a mirror would). For the document step it must stay unmirrored — a
 * mirrored live preview makes ID card text unreadable while the student is
 * trying to align it, even though the actual captured frame is never flipped
 * either way (see `captureStillFromVideo`).
 */
function previewVideoStyle(mode: IdCaptureMode) {
  return mode === 'selfie'
    ? { ...PREVIEW_VIDEO_BASE_STYLE, transform: 'scaleX(-1)' as const }
    : PREVIEW_VIDEO_BASE_STYLE;
}

export type IdVerificationStepProps = {
  mode: IdCaptureMode;
  onCapture: (imageBase64: string) => void;
  isSubmitting?: boolean;
  submitted?: boolean;
  /** Pre-existing image to show on the "awaiting review" screen (e.g. the last
   * captured frame from the parent), used when this instance has no local
   * `capturedPreview` of its own (a fresh mount into an already-submitted step). */
  previewImage?: string | null;
  /** Hide the Retake affordance once the photo is truly submitted and the ball
   * is with the reviewer — retaking at that point would silently invalidate a
   * review already in flight. Defaults to true (retake allowed mid-capture). */
  allowRetake?: boolean;
};

export type CropRect = { sx: number; sy: number; sWidth: number; sHeight: number };

/**
 * Map a fractional guide rect (fractions of the preview container) to a pixel
 * crop rect in the raw sensor frame, accounting for the `object-fit: cover` the
 * preview uses.
 *
 * Under `cover`, the container shows only the centred sub-rectangle of the
 * sensor whose aspect matches the container (`ID_CAPTURE_PREVIEW_ASPECT`); the
 * rest is clipped off-screen. So the fractions the student aligns to are
 * fractions of THAT visible sub-rectangle, not of the whole sensor. We
 * reconstruct the visible sub-rectangle, then take the guide fractions inside
 * it. This makes the saved crop exactly the outline the student saw — no DOM
 * measurement, no drift.
 *
 * Exported for unit tests.
 */
export function resolveGuideCropRect(
  sourceWidth: number,
  sourceHeight: number,
  mode: IdCaptureMode,
): CropRect {
  const rect = ID_CAPTURE_GUIDE_RECTS[mode];
  const sourceAspect = sourceWidth / sourceHeight;

  // Visible sub-rectangle of the sensor under `object-fit: cover`.
  let visibleWidth = sourceWidth;
  let visibleHeight = sourceHeight;
  let visibleX = 0;
  let visibleY = 0;
  if (sourceAspect > ID_CAPTURE_PREVIEW_ASPECT) {
    // Sensor wider than the container: sides are clipped, full height shown.
    visibleWidth = sourceHeight * ID_CAPTURE_PREVIEW_ASPECT;
    visibleX = (sourceWidth - visibleWidth) / 2;
  } else {
    // Sensor taller than the container: top/bottom clipped, full width shown.
    visibleHeight = sourceWidth / ID_CAPTURE_PREVIEW_ASPECT;
    visibleY = (sourceHeight - visibleHeight) / 2;
  }

  return {
    sx: visibleX + rect.x * visibleWidth,
    sy: visibleY + rect.y * visibleHeight,
    sWidth: rect.width * visibleWidth,
    sHeight: rect.height * visibleHeight,
  };
}

/**
 * Captures JPEG base64 from a video element, cropped to the mode's alignment
 * guide (see `resolveGuideCropRect`). Exported for crop/mirror-regression tests.
 *
 * Intentionally NOT flipped: per the MediaStream spec, `getUserMedia()` never
 * pre-mirrors the raw frame — the sensor's true (third-person) orientation is
 * always what `drawImage` reads, front camera or not. The "selfie mirror" the
 * student sees is applied purely via the preview's `scaleX(-1)` CSS transform
 * (see `previewVideoStyle` below, selfie-mode only) and never touches the underlying pixel
 * data, so this capture is already correctly oriented for the lecturer's
 * review. The guide rects are horizontally centred, so the mirror never shifts
 * which region is captured. (A canvas counter-flip was tried and reverted here
 * on 2026-07-07 — empirical "proof" via a pre-mirrored fake-camera fixture was
 * circular. If a lecturer genuinely reports a mirrored ID photo in production
 * after this note, that would point to unusual camera-driver behavior on that
 * specific device pre-mirroring the raw stream — confirm with that hardware
 * before changing this again, don't re-guess.)
 */
export async function captureStillFromVideo(
  video: HTMLVideoElement,
  mode: IdCaptureMode = 'selfie',
): Promise<string> {
  const sourceWidth = video.videoWidth || 640;
  const sourceHeight = video.videoHeight || 480;
  const {
    sx, sy, sWidth, sHeight,
  } = resolveGuideCropRect(sourceWidth, sourceHeight, mode);

  const canvas = document.createElement('canvas');
  canvas.width = sWidth;
  canvas.height = sHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.drawImage(video, sx, sy, sWidth, sHeight, 0, 0, sWidth, sHeight);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
  return dataUrl.split(',')[1] ?? '';
}

/** ID capture with mirrored preview + step-specific alignment overlays. */
export function IdVerificationStep({
  mode,
  onCapture,
  isSubmitting = false,
  submitted = false,
  previewImage = null,
  allowRetake = true,
}: IdVerificationStepProps) {
  const { t } = useTranslation();
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [capturedPreview, setCapturedPreview] = useState<string | null>(null);
  const [isRetaking, setIsRetaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraRetryToken, setCameraRetryToken] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const displayPreview = capturedPreview ?? previewImage ?? null;
  const showPreview = (Boolean(displayPreview) || submitted) && !isRetaking;
  const showCamera = !showPreview;
  const canRetake = allowRetake && !submitted;

  const copyPrefix = mode === 'document' ? 'oralExam.idCapture.document' : 'oralExam.idCapture.selfie';

  useEffect(() => {
    // Feature-detect rather than platform-branch: oral exams are hardwired to
    // web (the native app is blocked upstream by `OralExamMobileAppNotice`),
    // so the only reason `mediaDevices` would be missing here is a browser
    // without camera APIs (or a test harness that hasn't mocked one).
    if (!showCamera || typeof navigator === 'undefined' || !navigator.mediaDevices) return undefined;
    let stream: MediaStream | null = null;
    let active = true;
    navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      .then((s) => {
        stream = s;
        if (active) setCameraStream(s);
      })
      .catch((e: unknown) => {
        if (active) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      active = false;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [showCamera, mode, cameraRetryToken]);

  useEffect(() => {
    if (!videoRef.current || !cameraStream) return;
    videoRef.current.srcObject = cameraStream;
    videoRef.current.play().catch(() => {});
  }, [cameraStream]);

  const handleCapture = async () => {
    setError(null);
    // Defensive only: the capture button is disabled until `cameraStream` is
    // ready. Ref can lag one frame (common in RN web tests); fall back to a
    // dimensioned stub element so captureStillFromVideo can still run.
    let videoEl = videoRef.current;
    if (!videoEl && cameraStream) {
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });
      videoEl = videoRef.current;
    }
    if (!videoEl && cameraStream) {
      videoEl = document.createElement('video');
      Object.defineProperty(videoEl, 'videoWidth', { value: 640, configurable: true });
      Object.defineProperty(videoEl, 'videoHeight', { value: 480, configurable: true });
    }
    if (!videoEl) {
      setError(t('oralExam.idCapture.captureUnavailable', { defaultValue: 'Camera preview not ready. Try again.' }));
      return;
    }
    try {
      const imageBase64 = await captureStillFromVideo(videoEl, mode);
      setCapturedPreview(imageBase64);
      setIsRetaking(false);
      cameraStream?.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
      onCapture(imageBase64);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleRetake = () => {
    setCapturedPreview(null);
    setIsRetaking(true);
    setError(null);
  };

  const handleRetryCamera = () => {
    setError(null);
    setIsRetaking(false);
    setCapturedPreview(null);
    setCameraStream(null);
    setCameraRetryToken((token) => token + 1);
  };

  if (showPreview) {
    return (
      <Center flex={1} p="$6" testID={`oral-exam-id-awaiting-${mode}`}>
        <VStack flex={1} space="md" maxWidth={560} width="100%" alignItems="center">
          <VStack flexShrink={0} space="md" alignItems="center" width="100%">
            <Box
              w={72}
              h={72}
              borderRadius="$full"
              bg="$bgBrandSubtle"
              borderWidth={2}
              borderColor="$borderBrand"
              alignItems="center"
              justifyContent="center"
              mb="$1"
            >
              <Box w={36} h={36} borderRadius="$full" bg="$brandBase" opacity={0.85} />
            </Box>

            <Text size="xl" fontWeight="semibold" color="$textHeading" textAlign="center">
              {t(`${copyPrefix}.awaitingTitle`, { defaultValue: t('oralExam.idCapture.awaitingTitle') })}
            </Text>
          </VStack>

          <Box
            flex={1}
            width="100%"
            minHeight={160}
            alignItems="center"
            justifyContent="center"
          >
            <Box
              width="100%"
              maxHeight="100%"
              borderRadius="$md"
              overflow="hidden"
              borderWidth={1}
              borderColor="$borderLight"
              bg="$bgSubtle"
              alignItems="center"
              justifyContent="center"
              style={{ aspectRatio: ID_CAPTURE_PREVIEW_ASPECT }}
            >
              {displayPreview ? (
                <img
                  src={`data:image/jpeg;base64,${displayPreview}`}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              ) : (
                <Text size="sm" color="$textSubtle" textAlign="center" px="$4">
                  {t('oralExam.idCapture.previewPlaceholder')}
                </Text>
              )}
            </Box>
          </Box>

          <VStack flexShrink={0} space="md" alignItems="center" width="100%">
            {canRetake && (
              <Button
                size="md"
                variant="outline"
                onPress={handleRetake}
                testID={`oral-exam-id-capture-retake-${mode}`}
              >
                <ButtonText>
                  {t('oralExam.idCapture.retake', { defaultValue: 'Retake' })}
                </ButtonText>
              </Button>
            )}

            <Text size="md" color="$textBody" textAlign="center">
              {t(`${copyPrefix}.awaitingBody`, { defaultValue: t('oralExam.idCapture.awaitingBody') })}
            </Text>
          </VStack>
        </VStack>
      </Center>
    );
  }

  /* eslint-disable react-native/no-color-literals */
  const guideDimStyle = {
    boxShadow: '0 0 0 2000px rgba(8, 12, 18, 0.55)',
  } as const;
  /* eslint-enable react-native/no-color-literals */

  return (
    <Box flex={1} p="$4" testID={`oral-exam-id-capture-step-${mode}`}>
      <VStack flex={1} space="sm" maxWidth={480} width="100%" alignSelf="center" alignItems="center">
        <VStack flexShrink={0} space="sm" alignItems="center" width="100%">
          <IdCheckProgress current={mode} />
          <Text size="xs" color="$textHint" fontFamily="$heading" textTransform="uppercase">
            {t(`${copyPrefix}.eyebrow`)}
          </Text>
          <Text size="xl" fontFamily="$heading" color="$textHeading" textAlign="center">
            {t(`${copyPrefix}.title`)}
          </Text>
          <Text size="md" textAlign="center">
            {t(`${copyPrefix}.body`)}
          </Text>
          {mode === 'document' ? <AcceptedDocuments /> : <FaceDoDont />}
        </VStack>

        <Box
          flex={1}
          width="100%"
          minHeight={160}
          alignItems="center"
          justifyContent="center"
        >
          <Box
            width="100%"
            maxHeight="100%"
            borderRadius="$md"
            overflow="hidden"
            borderWidth={1}
            borderColor="$borderLight"
            position="relative"
            style={{ aspectRatio: ID_CAPTURE_PREVIEW_ASPECT }}
            testID={`oral-exam-id-preview-container-${mode}`}
          >
            {cameraStream ? (
              <>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video ref={videoRef as never} muted playsInline style={previewVideoStyle(mode)} />
                <Box
                  position="absolute"
                  left={`${ID_CAPTURE_GUIDE_RECTS[mode].x * 100}%`}
                  top={`${ID_CAPTURE_GUIDE_RECTS[mode].y * 100}%`}
                  width={`${ID_CAPTURE_GUIDE_RECTS[mode].width * 100}%`}
                  height={`${ID_CAPTURE_GUIDE_RECTS[mode].height * 100}%`}
                  borderWidth={3}
                  borderColor="$borderFocus"
                  borderRadius={mode === 'selfie' ? '$full' : '$md'}
                  pointerEvents="none"
                  style={guideDimStyle}
                />
                <Box
                  position="absolute"
                  left={`${ID_CAPTURE_GUIDE_RECTS[mode].x * 100}%`}
                  top={`${(ID_CAPTURE_GUIDE_RECTS[mode].y + ID_CAPTURE_GUIDE_RECTS[mode].height) * 100 + 1}%`}
                  width={`${ID_CAPTURE_GUIDE_RECTS[mode].width * 100}%`}
                  alignItems="center"
                  pointerEvents="none"
                >
                  <Text size="xs" color="$textInverted" textAlign="center">
                    {t(`${copyPrefix}.frameLabel`)}
                  </Text>
                </Box>
              </>
            ) : (
              <Box flex={1} alignItems="center" justifyContent="center" bg="$bgSubtle" px="$4">
                <Text size="sm" color="$textSubtle" textAlign="center">
                  {t('oralExam.idCapture.previewPlaceholder')}
                </Text>
              </Box>
            )}
          </Box>
        </Box>

        <VStack flexShrink={0} space="sm" alignItems="center" width="100%">
          {!!error && (
            <VStack space="xs" alignItems="center" width="100%">
              <Text size="sm" color="$statusError" testID={`oral-exam-id-capture-error-${mode}`}>
                {error}
              </Text>
              <Button
                size="sm"
                variant="outline"
                onPress={handleRetryCamera}
                testID={`oral-exam-id-capture-retry-camera-${mode}`}
              >
                <ButtonText>
                  {t('oralExam.idCapture.retryCamera', { defaultValue: 'Retry camera' })}
                </ButtonText>
              </Button>
            </VStack>
          )}

          <Button
            size="md"
            onPress={handleCapture}
            isDisabled={isSubmitting || !cameraStream}
            testID={`oral-exam-id-capture-submit-${mode}`}
            width="100%"
            maxWidth={280}
          >
            <ButtonText>{t(`${copyPrefix}.capture`)}</ButtonText>
          </Button>
        </VStack>
      </VStack>
    </Box>
  );
}
